import React, { useState } from 'react';
import { Card, Text, Button, ButtonGroup } from '@fragments';
import {
    IconBuildingBank,
    IconChartLine,
    IconClockDollar,
    IconHome,
    IconReceiptRefund,
} from '@tabler/icons-react';
import './styles.scss';

/**
 * RecentTransactionsTabbed — faithful replica of the AlignUI finance dashboard
 * "Recent Transactions" card.
 *
 * Flat <Card> (crisp white on the gray canvas) with a "See All" text Button in
 * the header action slot. The body opens with a full-width segmented tab control
 * (Incoming / Outgoing / Pending) and then a four-row transaction list. Rows are
 * separated by clean full-width hairlines (border-bottom on every row but the
 * last), each leading with a neutral rounded-square avatar chip, a name/sub text
 * column, and a right-aligned amount-over-date column.
 *
 * Amounts read as strong ink (Text.label contrast 9) — no green/red painting —
 * matching the restraint of the source dashboard.
 */

// ── Mock data (inline, self-contained) ──────────────────────────────────────
// Verbatim content from the AlignUI dashboard: names, descriptions, USD amounts
// and dates exactly as shown.
const TRANSACTIONS = [
    {
        id: 'salary',
        Icon: IconBuildingBank,
        name: 'Salary Deposit',
        sub: 'Monthly salary from Apex Finance',
        amount: '$3,500.00',
        date: 'September 18, 2024',
    },
    {
        id: 'dividend',
        Icon: IconChartLine,
        name: 'Stock Dividend',
        sub: 'Payment from stock investments.',
        amount: '$846.14',
        date: 'September 17, 2024',
    },
    {
        id: 'rental',
        Icon: IconHome,
        name: 'Rental Income',
        sub: 'Rental payment from Mr. Dudley.',
        amount: '$100.00',
        date: 'September 15, 2024',
    },
    {
        id: 'refund',
        Icon: IconReceiptRefund,
        name: 'Refund from Amazon',
        sub: 'Refund of Order No #124235',
        amount: '$36.24',
        date: 'September 12, 2024',
    },
];

const TABS = [
    { label: 'Incoming', value: 'in' },
    { label: 'Outgoing', value: 'out' },
    { label: 'Pending', value: 'pend' },
];

export default function RecentTransactionsTabbed() {
    const [tab, setTab] = useState('in');

    return (
        <Card
            flat
            title="Recent Transactions"
            icon={<IconClockDollar />}
            action={
                <Button color="inverse" variant="text" size="small">
                    See All
                </Button>
            }
        >
            {/* Full-width segmented tabs sit at the top of the body. */}
            <div className="rtt__tabs">
                <ButtonGroup
                    grow
                    size="small"
                    options={TABS}
                    value={tab}
                    onChange={setTab}
                />
            </div>

            <ul className="rtt__list">
                {TRANSACTIONS.map(({ id, Icon, name, sub, amount, date }) => (
                    <li key={id} className="rtt__row">
                        <span className="rtt__chip" aria-hidden="true">
                            <Icon />
                        </span>

                        <div className="rtt__info">
                            <Text.label contrast={9} maxLines={1}>
                                {name}
                            </Text.label>
                            <Text.caption contrast={6} maxLines={1}>
                                {sub}
                            </Text.caption>
                        </div>

                        <div className="rtt__trailing">
                            <Text.label contrast={9} maxLines={1}>{amount}</Text.label>
                            <Text.caption contrast={5}>{date}</Text.caption>
                        </div>
                    </li>
                ))}
            </ul>
        </Card>
    );
}
